import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getDailyWord,
  getRandomWord,
  getPuzzleNumber,
  getDateKey,
  evaluateGuess,
  isValidGuessAsync,
  preloadValidGuesses,
  WORD_LENGTH,
  MAX_GUESSES,
} from './wordMasterUtils';
import { useAuth } from '../../context/AuthContext';
import {
  getDailyWordMasterLeaderboard,
  getUserDailyWordMasterResult,
  recordWordMasterDailyCompletion,
} from '../../firebase/wordMasterService';
import CozyPlayShell from '../../components/CozyPlayShell/CozyPlayShell';
import { HowToPlay } from './HowToPlay';
import './WordMaster.css';

const HOW_TO_PLAY_KEY = 'wordMasterHowToPlaySeen';
const DAILY_PLAYED_KEY = 'wordMasterDailyPlayed';

function WordMaster() {
  const { user, profile } = useAuth();
  const [mode, setMode] = useState('daily'); // 'daily' | 'practice'
  const [targetWord, setTargetWord] = useState(() => getDailyWord());
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState('');
  const [evaluations, setEvaluations] = useState([]);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [flippingRow, setFlippingRow] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [dailyAlreadyPlayed, setDailyAlreadyPlayed] = useState(null); // null = loading/not daily, { won, attempts? } = already played
  const inputRef = useRef(null);
  const latestInputRef = useRef('');
  const messageTimeoutRef = useRef(null);
  /** UX-only snapshots when switching Daily ↔ Practice mid-round (parity with Flutter). */
  const dailySnapRef = useRef(null);
  const practiceSnapRef = useRef(null);

  const isDaily = mode === 'daily';

  useEffect(() => {
    preloadValidGuesses();
  }, []);

  useEffect(() => {
    const seen = localStorage.getItem(HOW_TO_PLAY_KEY);
    if (!seen) setShowHowToPlay(true);
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, []);

  // Check if user has already played today's daily (one-shot)
  useEffect(() => {
    if (!isDaily) {
      setDailyAlreadyPlayed(null);
      return;
    }
    setDailyAlreadyPlayed('loading');
    if (user) {
      getUserDailyWordMasterResult(user.uid)
        .then((result) => setDailyAlreadyPlayed(result ? { ...result } : null))
        .catch(() => setDailyAlreadyPlayed(null));
    } else {
      const dateKey = getDateKey();
      const stored = localStorage.getItem(`${DAILY_PLAYED_KEY}_${dateKey}`);
      if (!stored) {
        setDailyAlreadyPlayed(null);
        return;
      }
      try {
        const parsed = JSON.parse(stored);
        setDailyAlreadyPlayed(typeof parsed === 'object' ? parsed : { won: false });
      } catch {
        setDailyAlreadyPlayed({ won: false });
      }
    }
  }, [isDaily, user]);

  useEffect(() => {
    if (isDaily && (gameOver || (dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading'))) {
      getDailyWordMasterLeaderboard(10).then(setLeaderboard);
    } else if (!isDaily) {
      setLeaderboard([]);
    }
  }, [isDaily, gameOver, dailyAlreadyPlayed]);

  useEffect(() => {
    if (!gameOver && targetWord) inputRef.current?.focus();
  }, [gameOver, targetWord, guesses]);

  const showMessage = useCallback((msg, duration = 2000) => {
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    setMessage(msg);
    messageTimeoutRef.current = setTimeout(() => {
      setMessage('');
      messageTimeoutRef.current = null;
    }, duration);
  }, []);

  const saveProgress = useCallback(async (won, attempts) => {
    if (isDaily) {
      // Guests: mark played in localStorage (one-shot)
      if (!user) {
        const dateKey = getDateKey();
        localStorage.setItem(`${DAILY_PLAYED_KEY}_${dateKey}`, JSON.stringify({ won, attempts }));
        return;
      }
      try {
        // Flutter parity path: record daily completion + update profile + leaderboards.
        const displayName =
          profile?.displayName?.trim() ||
          user.displayName?.trim() ||
          user.email?.split('@')[0] ||
          'Player';
        await recordWordMasterDailyCompletion({
          uid: user.uid,
          dateId: getDateKey(),
          guessCount: attempts,
          win: won,
          displayName,
        });
      } catch (err) {
        console.error('Failed to save progress:', err);
      }
    }
  }, [user, profile, isDaily]);

  const submitGuess = useCallback(async (valueOverride) => {
    if (!targetWord || gameOver) return;
    if (isDaily && dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading') return;
    // Prefer latestInputRef (updated on every keystroke) - React state lags on fast typing + Enter
    const rawValue = valueOverride ?? latestInputRef.current ?? currentGuess;
    const raw = String(rawValue).toUpperCase().replace(/[^A-Z]/g, '');
    const guess = raw.slice(0, WORD_LENGTH);
    if (guess.length !== WORD_LENGTH) {
      showMessage('Word must be 5 letters');
      return;
    }
    if (!(await isValidGuessAsync(guess))) {
      showMessage('Not in word list');
      return;
    }

    const evaluation = evaluateGuess(guess, targetWord);
    const newGuesses = [...guesses, guess];
    const newEvaluations = [...evaluations, evaluation];

    setGuesses(newGuesses);
    setEvaluations(newEvaluations);
    setCurrentGuess('');
    latestInputRef.current = '';
    setFlippingRow(guesses.length);

    const won = guess === targetWord;
    if (won) {
      setGameOver(true);
      if (isDaily) {
        setDailyAlreadyPlayed({ won: true, attempts: newGuesses.length });
        dailySnapRef.current = null;
      }
      saveProgress(true, newGuesses.length);
      showMessage('🎉 Correct!', 3000);
    } else if (newGuesses.length >= MAX_GUESSES) {
      setGameOver(true);
      if (isDaily) {
        setDailyAlreadyPlayed({ won: false, attempts: MAX_GUESSES });
        dailySnapRef.current = null;
      }
      saveProgress(false, MAX_GUESSES);
      showMessage(`The word was ${targetWord}`, 4000);
    }
  }, [targetWord, currentGuess, guesses, evaluations, gameOver, showMessage, saveProgress, isDaily, dailyAlreadyPlayed]);

  useEffect(() => {
    if (flippingRow === null) return;
    const timer = setTimeout(() => setFlippingRow(null), 600);
    return () => clearTimeout(timer);
  }, [flippingRow]);

  const handleKeyDown = useCallback((e) => {
    if (gameOver) return;
    const isInputFocused = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent window listener from firing again (would see cleared ref)
      submitGuess(latestInputRef.current);
      return;
    }
    if (isInputFocused) return; // Let the input handle letters and Backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = (latestInputRef.current || currentGuess).slice(0, -1);
      latestInputRef.current = next;
      setCurrentGuess(next);
      setMessage('');
    } else if (/^[A-Za-z]$/.test(e.key)) {
      e.preventDefault();
      const curr = latestInputRef.current || currentGuess;
      const next = curr.length < WORD_LENGTH ? curr + e.key.toUpperCase() : curr;
      latestInputRef.current = next;
      setCurrentGuess(next);
      setMessage('');
    }
  }, [gameOver, submitGuess, currentGuess]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, WORD_LENGTH);
    latestInputRef.current = value;
    setCurrentGuess(value);
    if (message) setMessage(''); // Dismiss message when user types
  };

  const switchMode = (newMode) => {
    if (newMode === mode) return;
    const wasDaily = mode === 'daily';

    const dailyBlocked =
      dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading';

    function clearBoardFresh() {
      setGuesses([]);
      setCurrentGuess('');
      latestInputRef.current = '';
      setGameOver(false);
      setMessage('');
      setEvaluations([]);
      setFlippingRow(null);
    }

    if (wasDaily && !dailyBlocked && !gameOver) {
      dailySnapRef.current = {
        dateKey: getDateKey(),
        targetWord,
        guesses: guesses.map((g) => g),
        evaluations: evaluations.map((row) => row.slice()),
        gameOver,
        flippingRow,
        currentGuess: latestInputRef.current || currentGuess,
      };
    }
    if (!wasDaily && !gameOver) {
      practiceSnapRef.current = {
        targetWord,
        guesses: guesses.map((g) => g),
        evaluations: evaluations.map((row) => row.slice()),
        gameOver,
        flippingRow,
        currentGuess: latestInputRef.current || currentGuess,
      };
    }

    const willDaily = newMode === 'daily';
    setMode(newMode);
    clearBoardFresh();

    if (willDaily) {
      if (dailyBlocked) {
        dailySnapRef.current = null;
        setTargetWord(getDailyWord());
        setLeaderboard([]);
      } else {
        const snap = dailySnapRef.current;
        const key = getDateKey();
        const canonical = getDailyWord();
        if (snap && snap.dateKey === key && snap.targetWord === canonical) {
          setTargetWord(snap.targetWord);
          setGuesses(snap.guesses.map((g) => g));
          setEvaluations(snap.evaluations.map((row) => row.slice()));
          setGameOver(snap.gameOver);
          setFlippingRow(snap.flippingRow);
          latestInputRef.current = snap.currentGuess || '';
          setCurrentGuess(snap.currentGuess || '');
        } else {
          dailySnapRef.current = null;
          setTargetWord(canonical);
        }
      }
    } else {
      const snap = practiceSnapRef.current;
      if (snap) {
        setTargetWord(snap.targetWord);
        setGuesses(snap.guesses.map((g) => g));
        setEvaluations(snap.evaluations.map((row) => row.slice()));
        setGameOver(snap.gameOver);
        setFlippingRow(snap.flippingRow);
        latestInputRef.current = snap.currentGuess || '';
        setCurrentGuess(snap.currentGuess || '');
      } else {
        practiceSnapRef.current = null;
        setTargetWord(getRandomWord());
        setLeaderboard([]);
      }
    }

    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const resetGame = () => {
    if (isDaily && dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading') return;
    setGuesses([]);
    setCurrentGuess('');
    latestInputRef.current = '';
    setGameOver(false);
    setMessage('');
    setEvaluations([]);
    practiceSnapRef.current = null;
    setTargetWord(isDaily ? getDailyWord() : getRandomWord());
    if (isDaily) setLeaderboard([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const getShareText = () => {
    const puzzleNum = isDaily ? getPuzzleNumber() : '∞';
    const won = guesses[guesses.length - 1] === targetWord;
    const resultLine = won ? `${guesses.length}/${MAX_GUESSES}` : 'X/6';
    const statusMap = { correct: '🟩', present: '🟨', absent: '⬜' };
    const gridLines = evaluations.map((row) =>
      row.map((s) => statusMap[s] || '⬜').join('')
    );
    return `Word Master ${puzzleNum} ${resultLine}\n\n${gridLines.join('\n')}\n\n${window.location.origin}${window.location.pathname}`;
  };

  const handleShareResult = async () => {
    try {
      await navigator.clipboard.writeText(getShareText());
      showMessage('Copied to clipboard!');
    } catch (err) {
      showMessage('Failed to copy');
    }
  };

  const handleHowToPlayClose = () => {
    setShowHowToPlay(false);
    localStorage.setItem(HOW_TO_PLAY_KEY, 'true');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  if (!targetWord) return <div className="word-master-loading">Loading...</div>;

  return (
    <CozyPlayShell
      className="word-master"
      title="Word Master"
      mode={mode}
      onModeChange={switchMode}
      onHowToPlay={() => setShowHowToPlay(true)}
      subtitle={
        isDaily
          ? `Puzzle #${getPuzzleNumber()} – same word for everyone`
          : 'Type a 5-letter word and press Enter'
      }
    >
      {showHowToPlay && <HowToPlay onClose={handleHowToPlayClose} />}
      {isDaily && !user && (
        <p className="word-master-signin-hint">Sign in to compete on today&apos;s leaderboard!</p>
      )}

      <div className={`word-master-board-wrap ${dailyAlreadyPlayed ? 'word-master-board-wrap--blocked' : ''}`}>
      {isDaily && dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading' && (
        <div className="word-master-daily-done">
          <p className="word-master-daily-done-title">
            {dailyAlreadyPlayed.won
              ? `You got it in ${dailyAlreadyPlayed.attempts}/${MAX_GUESSES}!`
              : "You've already played today's puzzle."}
          </p>
          <p className="word-master-daily-done-msg">Come back tomorrow for a new puzzle!</p>
          <div className="word-master-daily-done-actions">
            <button
              type="button"
              className="word-master-play-practice-btn"
              onClick={() => switchMode('practice')}
            >
              Play Practice
            </button>
            {gameOver && evaluations.length > 0 && (
              <button
                type="button"
                className="word-master-share"
                onClick={handleShareResult}
              >
                Share
              </button>
            )}
          </div>
        </div>
      )}
      <div className="word-master-board">
        {Array.from({ length: MAX_GUESSES }, (_, rowIndex) => (
          <div key={rowIndex} className="word-master-row">
            {Array.from({ length: WORD_LENGTH }, (_, colIndex) => {
              let letter = '';
              let status = '';
              if (rowIndex < guesses.length) {
                letter = guesses[rowIndex][colIndex] || '';
                status = evaluations[rowIndex]?.[colIndex] || '';
              } else if (rowIndex === guesses.length) {
                letter = currentGuess[colIndex] || '';
              }
              const isFlipping = flippingRow === rowIndex && status;
              const hasFlip = status && (rowIndex < guesses.length || isFlipping);
              return (
                <div
                  key={colIndex}
                  className={`word-master-cell ${status} ${letter ? 'filled' : ''} ${isFlipping ? 'flipping' : ''} ${hasFlip ? 'has-flip' : ''}`}
                  style={isFlipping ? { animationDelay: `${colIndex * 75}ms` } : undefined}
                >
                  {hasFlip ? (
                    <div className="word-master-cell-inner">
                      <div className="word-master-cell-front">{letter}</div>
                      <div className={`word-master-cell-back ${status}`}>{letter}</div>
                    </div>
                  ) : (
                    letter
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </div>

      {!(isDaily && dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading') && (
      <div className="word-master-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="word-master-input"
          placeholder="Type your word..."
          value={currentGuess}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={gameOver || (isDaily && dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading')}
          maxLength={WORD_LENGTH}
          autoComplete="off"
          autoCapitalize="characters"
          aria-label="Type your 5-letter guess"
        />
        <span className="word-master-input-hint">Press Enter to submit</span>
      </div>
      )}

      {message && (
        <div className="word-master-message word-master-toast" role="status">
          {message}
        </div>
      )}

      {gameOver && !(isDaily && dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading') && (
        <div className="word-master-game-over">
          <button
            className="word-master-play-again"
            onClick={resetGame}
          >
            {isDaily ? 'Play Again' : 'New Word'}
          </button>
          <button
            className="word-master-share"
            onClick={handleShareResult}
          >
            Share
          </button>
        </div>
      )}

      {isDaily && (gameOver || (dailyAlreadyPlayed && dailyAlreadyPlayed !== 'loading')) && leaderboard.length > 0 && (
        <div className="word-master-leaderboard">
          <h3>Today&apos;s leaderboard</h3>
          <ol>
            {leaderboard.map((ent, i) => (
              <li key={ent.uid}>
                <span className="word-master-leaderboard-rank">{i + 1}</span>
                <span className="word-master-leaderboard-name">{ent.displayName}</span>
                <span className="word-master-leaderboard-attempts">{ent.attempts}/6</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </CozyPlayShell>
  );
}

export default WordMaster;
